import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Instagram, Youtube } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import ContactModal from "@/components/ContactModal";
import { CONTACT_EMAIL, LEGAL_VERSION } from "@/legal/content";

// SoundCloud isn't in lucide-react, so we ship a compact inline SVG that
// matches the stroke-based visual weight of Instagram/Youtube icons.
// Path adapted from Simple Icons (CC0) with `currentColor` fill so it
// inherits the surrounding text color and hover transitions cleanly.
const SoundCloudIcon = ({ size = 16, className = "" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    width={size}
    height={size}
    aria-hidden="true"
    fill="currentColor"
    className={className}
  >
    <path d="M23.999 14.165c-.052 1.796-1.612 3.169-3.401 3.169h-8.18a.68.68 0 01-.68-.68V7.822a.75.75 0 01.451-.699c.53-.227 1.11-.351 1.719-.351 2.42 0 4.404 1.837 4.671 4.192.375-.155.78-.242 1.211-.242 1.766 0 3.209 1.432 3.209 3.202 0 .081-.007.16-.02.241zM10.312 17.334h.458V7.771a5.435 5.435 0 00-.458.271v9.292zm-1.375 0h.458V8.475c-.166.024-.323.05-.458.079v8.78zm-1.833 0h.916V8.943c-.334.203-.635.445-.916.71v7.681zm-1.833 0h.916v-7.51c-.35.428-.647.9-.916 1.409v6.101zm-1.833 0h.916v-4.928c-.412.663-.708 1.416-.847 2.229l-.069.404v2.295zM.667 12.667c-.32.412-.514.929-.514 1.501 0 .572.194 1.089.514 1.501v-3.002z" />
  </svg>
);

export default function LegalFooter() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const year = new Date().getFullYear();
  const [modal, setModal] = useState(null); // "contact" | "bug"

  // The in-app form requires login. Anonymous users still see a mailto fallback.
  const openContact = () => {
    if (user) setModal("contact");
    else window.location.href = `mailto:${CONTACT_EMAIL}`;
  };
  const openBug = () => {
    if (user) setModal("bug");
    else window.location.href = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent("[Spectraloom Bug Report]")}`;
  };

  const socials = [
    {
      key: "instagram",
      href: "https://www.instagram.com/spectraloom.app",
      label: "Instagram",
      Icon: Instagram,
    },
    {
      key: "youtube",
      href: "https://www.youtube.com/@SpectraloomApp",
      label: "YouTube",
      Icon: Youtube,
    },
    {
      key: "soundcloud",
      href: "https://soundcloud.com/spectraloomapp",
      label: "SoundCloud",
      Icon: SoundCloudIcon,
    },
  ];

  return (
    <footer
      className="border-t border-white/5 mt-16 py-8 px-6 lg:px-10"
      data-testid="legal-footer"
    >
      <div className="max-w-[1400px] mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-xs text-[color:var(--fg-muted)]">
        <div className="leading-relaxed">
          <div>© {year} Spectraloom · {t("footer.tagline")} · {t("footer.rights_reserved")}</div>
          <div className="opacity-80">{t("footer.company_info")}</div>
        </div>
        <nav className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <Link to="/legal/terms" className="hover:text-white transition-colors" data-testid="footer-terms">
            {t("footer.terms")}
          </Link>
          <Link to="/legal/privacy" className="hover:text-white transition-colors" data-testid="footer-privacy">
            {t("footer.privacy")}
          </Link>
          <Link to="/legal/cookies" className="hover:text-white transition-colors" data-testid="footer-cookies">
            {t("footer.cookies")}
          </Link>
          <Link to="/legal/refund" className="hover:text-white transition-colors" data-testid="footer-refund">
            {t("footer.refund")}
          </Link>
          <button
            onClick={openContact}
            className="hover:text-white transition-colors bg-transparent border-0 p-0 cursor-pointer"
            data-testid="footer-contact"
          >
            {t("footer.contact")}
          </button>
          <button
            onClick={openBug}
            className="hover:text-amber-300 transition-colors inline-flex items-center gap-1 bg-transparent border-0 p-0 cursor-pointer"
            data-testid="footer-report-bug"
            title={t("footer.report_bug_title")}
          >
            🐛 {t("footer.report_bug")}
          </button>
          <span className="opacity-60">v{LEGAL_VERSION} · app 2.0</span>

          {/* Social icons · small pill row, same muted color as the other
              footer links, subtle brand-tinted hover. Rendered inline with
              the legal nav so nothing shifts on mobile: on narrow screens
              the flex-wrap simply pushes the icons onto the next line. */}
          <span className="hidden sm:inline-block h-3 w-px bg-white/10 mx-1" aria-hidden="true" />
          <div
            className="flex items-center gap-3"
            data-testid="footer-socials"
            aria-label={t("footer.follow_us")}
          >
            {socials.map(({ key, href, label, Icon }) => (
              <a
                key={key}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                title={label}
                data-testid={`footer-social-${key}`}
                className="hover:text-white transition-colors"
              >
                <Icon size={16} />
              </a>
            ))}
          </div>
        </nav>
      </div>

      <ContactModal
        open={modal === "contact"}
        onClose={() => setModal(null)}
        category="general"
        defaultSubject=""
        kicker={t("footer.contact")}
      />
      <ContactModal
        open={modal === "bug"}
        onClose={() => setModal(null)}
        category="bug_report"
        defaultSubject={t("footer.bug_default_subject")}
        kicker={t("footer.report_bug")}
      />
    </footer>
  );
}
